import { useState } from "react";
import { Link } from "react-router-dom";
import { BrainCircuit, Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import { canAccessContractorKnowledge } from "../utils";
import { useContractorKnowledgeNotes, useKnowledgeNoteMutations } from "../api/knowledgeNotes";
import { KnowledgeNoteForm } from "./KnowledgeNoteForm";
import { KnowledgeNotesList } from "./KnowledgeNotesList";

/**
 * „Zasady podpowiadania" na karcie podwykonawcy — zasady TEJ firmy (globalne żyją w
 * rejestrze zbiorczym, link poniżej). Widok wyłącznie dyrektora technicznego / CEO
 * (lustro backendowej bramki IsTechnicalDirector na /contractors/knowledge-notes/);
 * dla pozostałych sekcja nie istnieje i nic nie pobiera.
 */
export function ContractorKnowledgeNotesSection({ contractorId }: { contractorId: string }) {
  const user = useAuthStore((s) => s.user);
  const canManage = canAccessContractorKnowledge(user);
  const notesQ = useContractorKnowledgeNotes({ contractor: contractorId }, canManage);
  const { create } = useKnowledgeNoteMutations();
  const [adding, setAdding] = useState(false);

  if (!canManage) return null;

  const notes = notesQ.data ?? [];
  const activeCount = notes.filter((n) => n.in_force).length;
  const disabledCount = notes.filter((n) => !n.is_active).length;

  return (
    <section className="mb-6" data-testid="contractor-knowledge-notes-section">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <BrainCircuit size={16} className="text-[var(--color-text-muted)]" />
          Zasady podpowiadania
          {notesQ.data && (
            <span className="text-sm font-normal text-[var(--color-text-secondary)]">
              {activeCount} obowiązujących / {notes.length}
              {disabledCount > 0 && ` · ${disabledCount} wyłączonych`}
            </span>
          )}
        </h2>
        {canManage && !adding && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus size={13} /> Dodaj zasadę
          </Button>
        )}
      </div>

      <div className="bg-white p-5 dark:bg-gray-800">
        <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
          Wskazówki tekstowe, które system uwzględnia, podpowiadając tę firmę przy zlecaniu
          (WPW, MDCP, projekt techniczny, kosztorys, Wody Polskie, zwykłe zapotrzebowanie), np.
          „podpowiadaj przy WPW tylko, jeśli w ciągu ostatnich 8 h dostała zlecenie MDCP na tym
          samym projekcie” albo „urlop 1–15 sierpnia” (nie podpowiadaj w oknie dat). Zasady
          niczego nie blokują — ręczny wybór firmy zostaje. Przełącznik przy zasadzie ją
          wyłącza: zasada zostaje zapisana, ale nie jest brana pod uwagę, dopóki jej nie
          włączysz.{" "}
          <Link to="/contractors/suggestion-rules" className="underline hover:text-[var(--color-primary)]">
            Rejestr wszystkich zasad
          </Link>
        </p>

        {adding && (
          <div className="mb-3 rounded-lg border border-[var(--color-border)] p-3">
            <KnowledgeNoteForm
              fixedContractorId={contractorId}
              pending={create.isPending}
              error={create.isError ? "Nie udało się zapisać zasady." : null}
              onCancel={() => setAdding(false)}
              onSubmit={(payload) => create.mutate(payload, { onSuccess: () => setAdding(false) })}
            />
          </div>
        )}

        {notesQ.isLoading ? (
          <p className="py-3 text-sm text-[var(--color-text-secondary)]">Wczytywanie…</p>
        ) : (
          <KnowledgeNotesList
            notes={notes}
            canManage={canManage}
            fixedContractorId={contractorId}
            emptyText="Brak zasad dla tej firmy — obowiązują tylko zasady globalne z rejestru."
          />
        )}
      </div>
    </section>
  );
}
